import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button, Form } from "react-bootstrap";
import { service_url } from "../../Configs/mysqlconfig";
import { toast } from "react-toastify";
import fire from "../../Configs/firebase";
import moment from "moment";

function MTTransporterPaymentModal({
  show,
  setShow,
  route_Details,
  onSubmitReady,
  orderDeliveryDate,
  apiData = {},
  refreshApiData
}) {
  const [transporterCost, setTransporterCost] = useState("");
  const [transporterName, setTransporterName] = useState("");
  const [transporterOptions, setTransporterOptions] = useState([]);
  const [transporterId, setTransporterId] = useState("");
  const [selectedDate, setSelectedDate] = useState(moment(orderDeliveryDate).format("YYYY-MM-DD"));

  const [receipt, setReceipt] = useState(null);
  const [receiptName, setReceiptName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [matchedOrder, setMatchedOrder] = useState(null);

  const isDisabled = !!matchedOrder?.transporter_name;

  useEffect(() => {
    fire
      .database()
      .ref("Driver_Company")
      .once("value")
      .then((snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const companies = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
          }));

          const filteredTransporters = companies.filter(
            (company) =>
              company.status?.toLowerCase() === "active" &&
              company.type?.toLowerCase() === "outstation"
          );

          setTransporterOptions(filteredTransporters);
        } else {
          setTransporterOptions([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching transporters from Firebase:", error);
        toast("Failed to load transporters", { type: toast.TYPE.ERROR });
      });
  }, []);

  useEffect(() => {
    if (!Array.isArray(apiData) || !route_Details?.order_id) return;

    const baseOrderId = route_Details.order_id.split("-").slice(0, 2).join("/");

    const match = apiData.find(order => order.order_id === baseOrderId);

    if (match) {
      setMatchedOrder(match);
      setTransporterName(match.transporter_name || "");
      setTransporterId(match.transporter_id || "");
      setTransporterCost(match.transporter_cost || "");
      setReceiptName(match.receipt || "Already uploaded");
    }
  }, [apiData, route_Details]);

  function handleTransporterChange(event) {
    const selectedId = event.target.value;
    const selectedTransporter = transporterOptions.find(
      (t) => t.transporter_id == selectedId
    );

    if (selectedTransporter) {
      setTransporterId(selectedTransporter.transporter_id);
      setTransporterName(selectedTransporter.transporter_name);
    }
  }

  function handleFileChange(event) {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        setReceipt(file);
        setReceiptName(file.name);
      } else {
        toast("Please upload only image files", { type: toast.TYPE.ERROR });
        event.target.value = null;
      }
    }
  }

  function formatOrderId(orderId) {
    let parts = orderId.split("-");
    return parts.length >= 2 ? `${parts[0]}/${parts[1]}` : orderId;
  }

  const internalSubmit = () => {
    // Skip validation if data is already present (i.e., disabled)
    if (!isDisabled) {
      const isAnyFieldFilled = transporterId || transporterCost !== "" || receipt;
      if (isAnyFieldFilled) {
        if (!transporterId) {
          toast.error("Please select a transporter");
          return;
        }

        if (transporterCost === "" || isNaN(transporterCost)) {
          toast.error("Please enter a valid transporter cost");
          return;
        }

        if (!receipt) {
          toast.error("Please upload a receipt");
          return;
        }
      }
    }

    const formattedOrderId = formatOrderId(route_Details?.order_id);
    const payload = {
      transporterName: isDisabled ? null : (transporterName || null),
      transporterCost: isDisabled ? null : (transporterCost !== "" ? transporterCost : null),
      transporterId: isDisabled ? null : (transporterId || null),
      receipt: isDisabled ? null : (receipt || null),
      selectedDate,
      route_Details,
      setShow,
      formattedOrderId,
    };

    onSubmitReady(payload);

    if (typeof refreshApiData === "function") {
      refreshApiData();
    }
  };

  return (
    <Modal show={show} onHide={() => setShow(false)}>
      <Modal.Header closeButton>
        <Modal.Title>MT (Online) Transporter Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group>
            <Form.Label>Order ID</Form.Label>
            <Form.Control
              type="text"
              value={formatOrderId(route_Details?.order_id)}
              readOnly
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Route</Form.Label>
            <Form.Control type="text" value={route_Details?.driver_id} readOnly />
          </Form.Group>

          <Form.Group>
            <Form.Label>Transporter Name</Form.Label>
            {isDisabled ? (
              <Form.Control type="text" value={transporterName} readOnly />
            ) : (
              <select
                className="form-control"
                value={transporterId}
                onChange={handleTransporterChange}
              >
                <option value="">Select Transporter</option>
                {transporterOptions.map((transporter) => (
                  <option
                    key={transporter.transporter_id}
                    value={transporter.transporter_id}
                  >
                    {transporter.transporter_name}
                  </option>
                ))}
              </select>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label>Transporter Cost</Form.Label>
            <Form.Control
              type="number"
              value={transporterCost}
              disabled={isDisabled}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || parseFloat(value) >= 0) {
                  setTransporterCost(value);
                }
              }}
              min="0"
              onKeyDown={(e) => {
                if (e.key === "-") e.preventDefault();
              }}
              onWheel={(e) => e.target.blur()}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Order Delivery Date</Form.Label>
            <Form.Control type="date" value={selectedDate} readOnly />
          </Form.Group>

          <Form.Group>
            <Form.Label>Upload Receipt (Image)</Form.Label>
            <Form.Control
              type="file"
              accept="application/pdf,image/*"
              onChange={handleFileChange}
              disabled={isDisabled}
            />
            {receiptName && (
              <small className="text-muted">Selected file: {receiptName}</small>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={internalSubmit} disabled={isUploading}>
          {isUploading ? "Uploading..." : "Save Payment Details"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default MTTransporterPaymentModal;